import React, { useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import Collapse from '@mui/material/Collapse';

import TransparencySlider from './TransparencySlider';
import PointSizeSlider from './PointSizeSlider';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ContrastIcon from '@mui/icons-material/Contrast';

import {
    layerRequiresGis2TokenSelector,
    layerCanBeVisibleSelector,
    showColourPickerSelector,
    layerDefLabelsSelector,
    layerDefVisibilitySelector,
    layerDefsParentColourSelector,
    layerDefInfoSelector,
    layerDefFilterBySelectedRegionSelector,
    layerDefSourceDataSelector,
    layerDefsTypeSelector,
} from '../../layers/recoil';
import LayerInfo from './LayerInfo.jsx';
import axios from 'axios';
import {
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { CirclePicker } from 'react-color';
import { chartColoursList } from '../../themes/colours';

import { isLoggedInSelector } from '../../login/loginAtoms.js';

import CatchmentAreaLayerUpdater from '../../layers/hsds/catchment-area/CatchmentAreaLayerUpdater';
import TravelTimeControls from '../../layers/hsds/travel-time/TravelTimeControls';
import VisitorAndSpendControls from '../../layers/hsds/visitor-and-spend/VisitorAndSpendControls';
import LoacControls from '../../layers/hsds/loac/LoacControls';
import ImdLegend from '../../layers/hsds/imd/ImdLegend';

export default function LayerCheckbox({ layerID }) {
    const isLoggedIn = useRecoilValue(isLoggedInSelector);
    const requiresLogin = useRecoilValue(
        layerRequiresGis2TokenSelector(layerID),
    );

    const [collapseOpen, setCollapseOpen] = useState(false);
    const [layerCanBeVisible, setLayerCanBeVisible] = useRecoilState(
        layerCanBeVisibleSelector(layerID),
    );

    const [loading, setLoading] = useState(true);
    const filterBySelectedRegion = useRecoilValue(
        layerDefFilterBySelectedRegionSelector(layerID),
    );
    const layerUrl = useRecoilValue(layerDefSourceDataSelector(layerID));
    const layerType = useRecoilValue(layerDefsTypeSelector(layerID));
    const { parent: parentLabel } = useRecoilValue(
        layerDefLabelsSelector(layerID),
    );
    const [visibility, setVisibility] = useRecoilState(
        layerDefVisibilitySelector(layerID),
    );
    const info = useRecoilValue(layerDefInfoSelector(layerID));
    const [colour, setColour] = useRecoilState(
        layerDefsParentColourSelector(layerID),
    );
    const showColourPicker = useRecoilValue(showColourPickerSelector(layerID));

    const switchCollapseState = () => {
        setCollapseOpen((oldCollapseOpen) => !oldCollapseOpen);
    };

    const handleCheckboxClick = (event) => {
        setCollapseOpen(event.target.checked);
        setVisibility(event.target.checked);
    };

    const authorisedToViewLayers = !requiresLogin || isLoggedIn;
    const disabled = !layerCanBeVisible || !authorisedToViewLayers;
    const layerOnMap =
        visibility && layerCanBeVisible && authorisedToViewLayers;

    useEffect(() => {
        setLoading(true);

        if (!filterBySelectedRegion || layerType === 'raster') {
            setLayerCanBeVisible(true);
            setLoading(false);
            return;
        }

        if (!layerUrl) {
            setLayerCanBeVisible(false);
            setLoading(false);
            return;
        }

        axios
            .get(layerUrl)
            .then(function (response) {
                response.data.features &&
                    setLayerCanBeVisible(
                        Boolean(response.data.features.length > 0),
                    );
            })
            .finally(() => {
                setLoading(false);
            });
    }, [layerUrl, setLayerCanBeVisible, filterBySelectedRegion, layerType]);

    const getTooltipTitle = () => {
        if (requiresLogin && !isLoggedIn) {
            return 'HSDS login required to view this layer';
        }

        if (!layerCanBeVisible) {
            return 'No layer data available for the selected high street';
        }

        return '';
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                }}
            >
                <Tooltip arrow placement='left' title={getTooltipTitle()}>
                    <span>
                        <Checkbox
                            disabled={disabled}
                            icon={
                                loading ? (
                                    <CircularProgress size={20.57} />
                                ) : (
                                    <CircleOutlinedIcon />
                                )
                            }
                            checkedIcon={<CircleIcon />}
                            indeterminateIcon={<ContrastIcon />}
                            onChange={handleCheckboxClick}
                            checked={layerOnMap}
                            sx={{
                                p: 1,
                                color: colour,
                                '&.Mui-checked': {
                                    color: colour,
                                },
                            }}
                        />
                    </span>
                </Tooltip>
                <Typography
                    sx={{ opacity: !disabled ? 1 : 0.3 }}
                    {...(layerOnMap
                        ? {
                              onClick: switchCollapseState,
                              sx: { cursor: 'pointer' },
                          }
                        : {})}
                >
                    {parentLabel}
                </Typography>

                <LayerInfo {...info} />
                {layerOnMap && (
                    <IconButton onClick={switchCollapseState}>
                        {collapseOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
            </Box>

            {layerOnMap && (
                <Collapse in={collapseOpen}>
                    {layerID === 'travel-time' && (
                        <TravelTimeControls layerID={layerID} />
                    )}
                    {layerID === 'visitor-and-spend' && (
                        <VisitorAndSpendControls />
                    )}
                    {layerID === 'catchment-area' && (
                        <CatchmentAreaLayerUpdater />
                    )}
                    {layerID === 'imd' && <ImdLegend />}
                    {layerID === 'loac' && <LoacControls />}
                    {showColourPicker && (
                        <Box sx={{ pb: 2, pl: 3 }}>
                            <Typography
                                variant='caption'
                                id='colour-selector-title'
                            >
                                Select Colour:
                            </Typography>
                            <Box sx={{ pt: 1 }}>
                                <CirclePicker
                                    colors={chartColoursList}
                                    circleSize={22}
                                    width={'100%'}
                                    color={colour}
                                    onChange={(event) => {
                                        setColour(event.hex);
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    <TransparencySlider layerID={layerID} />
                    <PointSizeSlider layerID={layerID} />
                </Collapse>
            )}
        </>
    );
}
